import React, {PureComponent, Fragment, useContext, useRef} from "react"
import {matchPath, useLocation} from "react-router-dom"
import {omit} from "lodash"
import {Grid} from "@irati/ui/src/Layout"
import styled from "styled-components"
import Helmet from "react-helmet"

import Loading from "@irati/ui/src/Loading"
import Loader from "@irati/ui/src/Loading/Loader"
import {injectIntl, getMessage, InjectIntl} from "../../lang"
import {RouteHasPermissionTo} from "../../permissions"
import {AuthedCheckerContext, SetAuthStatus} from "./AuthedChecker"

const StyledLoader = styled.div.attrs({
  children: <Loader />
})`
  .loader {
    margin-top: 100px;
  }
`

const Div = (props) => <div {...props} />

export interface NestedRouteProps extends InjectIntl {
  permissions?: string | string[]
  exact?: boolean
  match: string
  path?: string
  title?: string
  values?: any
  component?: any
  componentProps?: {[key: string]: any}
  unauthed?: boolean
  noauth?: boolean
  useGrid?: boolean
}

/**
 * Core routed-page wrapper.
 *
 * Responsibilities:
 * 1. Builds the *full* route path by concatenating the parent `match` value
 *    with its own `path` prop so that nested routing works seamlessly.
 * 2. Runs permission & authentication checks (`RouteHasPermissionTo`,
 *    `AuthedCheckerContext`).
 * 3. Shows a loading spinner until auth status is known.
 * 4. Sets the document `<title>` via `react-helmet` using i18n aware helpers.
 * 5. Wraps the page component (`component`) in a Bootstrap `Grid` unless
 *    `useGrid` is set to `false`.
 *
 * All higher-order helpers (currently `withPartialRoute`) ultimately
 * render this component (or its `PartialRoute` variant), so understanding this
 * file explains the whole routed-page pipeline.
 */
class NestedRoute extends PureComponent<NestedRouteProps> {
  static defaultProps = {
    title: "",
    component: null,
    componentProps: null,
    unauthed: false,
    noauth: false,
    useGrid: true
  }

  render() {
    const {formatMessage, title, values, useGrid, component: App, componentProps, unauthed, noauth, permissions} = this.props

    const appProps = {...omit(this.props, "intl"), ...componentProps}

    const Wrapper = useGrid ? Grid : Div

    return (
      <AuthedCheckerContext.Consumer>
        {({load, setAuthed}) => (
          <Fragment>
            <SetAuthStatus setAuthed={setAuthed} unauthed={unauthed} noauth={noauth} />
            <RouteHasPermissionTo permissions={permissions}>
              <Loading load={load} Loader={() => <StyledLoader />}>
                <Helmet titleTemplate="Perkbox | %s">
                  <title>{title ? getMessage(formatMessage, title, values) : ""}</title>
                </Helmet>
                <Wrapper>
                  <App {...appProps} />
                </Wrapper>
              </Loading>
            </RouteHasPermissionTo>
          </Fragment>
        )}
      </AuthedCheckerContext.Consumer>
    )
  }
}

/**
 * Context used by `SwitchedRoutes` to give `NestedRoute` the same first-match-wins
 * behaviour that React Router v4's `<Switch>` provided.
 */
const SwitchedRoutesContext = React.createContext<{hasMatched: React.MutableRefObject<boolean>} | null>(null)

const Route: React.FC<NestedRouteProps> = (props) => {
  const {match: matchProp, path, exact, component, ...rest} = props
  const location = useLocation()
  const switchContext = useContext(SwitchedRoutesContext)
  const fullPath = `${matchProp}${path}`
  const pathMatch = matchPath({path: fullPath, end: !!exact}, location.pathname)
  if (!pathMatch) return null
  if (switchContext?.hasMatched.current) return null
  if (switchContext) switchContext.hasMatched.current = true

  // Build a v5-style match object with params so child components can access them
  const routeMatch = {
    params: pathMatch.params,
    url: pathMatch.pathname,
    path: pathMatch.pattern.path,
    isExact: pathMatch.pathname === location.pathname
  }

  const nestedProps = {match: routeMatch, path, exact, component, location, ...rest}
  return (
    <SwitchedRoutesContext.Provider value={null}>
      <NestedRoute {...nestedProps} />
    </SwitchedRoutesContext.Provider>
  )
}

Route.defaultProps = {
  match: "",
  path: "",
  useGrid: true
}

export default injectIntl(Route)

/**
 * Drop-in replacement for the React Router v4 `<Switch>` when wrapping a list
 * of `NestedRoute` (or `PartialRoute`) children.
 *
 * Renders only the **first** child whose path matches the current location —
 * identical to the exclusive-match guarantee that `Switch` used to provide.
 */
export const SwitchedRoutes: React.FC<React.PropsWithChildren> = ({children}) => {
  const hasMatched = useRef(false)
  // Reset on every render pass so a new location always re-evaluates all routes.
  // React renders siblings synchronously top-to-bottom, so the first matching
  // NestedRoute will set hasMatched.current = true before its siblings run.
  hasMatched.current = false
  return <SwitchedRoutesContext.Provider value={{hasMatched}}>{children}</SwitchedRoutesContext.Provider>
}
